export const SEMAFIX_HANDOFF_KEY = 'semafixHandoff';
export const SEMAFIX_HANDOFF_VERSION = 2;
export const SEMAFIX_HANDOFF_MAX_AGE_MS = 10 * 60 * 1000;

export function writeSemafixHandoff(payload) {
  const value = {
    ...payload,
    version: payload.version || SEMAFIX_HANDOFF_VERSION,
    from: 'semafix',
    createdAt: Date.now(),
  };
  sessionStorage.setItem(SEMAFIX_HANDOFF_KEY, JSON.stringify(value));
  return value;
}

export function readSemafixHandoff() {
  try {
    const value = JSON.parse(sessionStorage.getItem(SEMAFIX_HANDOFF_KEY) || 'null');
    if (value?.from !== 'semafix') return null;
    if (Number(value.createdAt) > 0
      && Date.now() - Number(value.createdAt) > SEMAFIX_HANDOFF_MAX_AGE_MS) return null;
    return value;
  } catch {
    return null;
  }
}

export function clearSemafixHandoff() {
  sessionStorage.removeItem(SEMAFIX_HANDOFF_KEY);
}

function corridorStops(handoff) {
  if (Array.isArray(handoff.corridor) && handoff.corridor.length > 0) return handoff.corridor;
  return [{
    intersectionId: handoff.intersectionId,
    signalId: handoff.signalId,
    name: handoff.name,
    lat: handoff.lat,
    lng: handoff.lng,
    green: handoff.green,
    yellow: handoff.yellow,
    red: handoff.red,
    offset: 0,
    distance: 0,
    crossingLength: handoff.crossingLength,
  }];
}

export function handoffPolygonPoints(handoff) {
  const ring = handoff?.areaPolygon?.geometry?.coordinates?.[0];
  if (!Array.isArray(ring)) return [];
  const points = ring
    .map((point) => [Number(point?.[1]), Number(point?.[0])])
    .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
  if (points.length > 1) {
    const [firstLat, firstLng] = points[0];
    const [lastLat, lastLng] = points[points.length - 1];
    if (firstLat === lastLat && firstLng === lastLng) points.pop();
  }
  return points.length >= 3 ? points : [];
}

export function syncfixPolygonBbox(points) {
  if (!Array.isArray(points) || points.length < 3) return null;
  const lats = points.map(([lat]) => Number(lat));
  const lngs = points.map(([, lng]) => Number(lng));
  if (![...lats, ...lngs].every(Number.isFinite)) return null;
  return `${Math.min(...lngs)},${Math.min(...lats)},${Math.max(...lngs)},${Math.max(...lats)}`;
}

export function isPointInSyncfixPolygon(light, polygon) {
  if (!Array.isArray(polygon) || polygon.length < 3) return true;
  const y = Number(light.lat);
  const x = Number(light.lon ?? light.lng);
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const [yi, xi] = polygon[index];
    const [yj, xj] = polygon[previous];
    const crosses = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (crosses) inside = !inside;
  }
  return inside;
}

const EARTH_RADIUS_M = 6371000;
const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;
const lngOf = (point) => Number(point.lon ?? point.lng);

function geoDistanceMeters(from, to) {
  const lat1 = toRadians(Number(from.lat));
  const lat2 = toRadians(Number(to.lat));
  const deltaLat = lat2 - lat1;
  const deltaLng = toRadians(lngOf(to) - lngOf(from));
  const value = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(value)));
}

function geoBearing(from, to) {
  const lat1 = toRadians(Number(from.lat));
  const lat2 = toRadians(Number(to.lat));
  const deltaLng = toRadians(lngOf(to) - lngOf(from));
  const y = Math.sin(deltaLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2)
    - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
  return (toDegrees(Math.atan2(y, x)) + 360) % 360;
}

function geoDestination(origin, bearing, meters) {
  const angular = meters / EARTH_RADIUS_M;
  const theta = toRadians(bearing);
  const lat1 = toRadians(Number(origin.lat));
  const lng1 = toRadians(lngOf(origin));
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular)
    + Math.cos(lat1) * Math.sin(angular) * Math.cos(theta),
  );
  const lng2 = lng1 + Math.atan2(
    Math.sin(theta) * Math.sin(angular) * Math.cos(lat1),
    Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
  );
  return { lat: toDegrees(lat2), lon: toDegrees(lng2) };
}

function closestLight(point, lights, excludedIds = new Set()) {
  const origin = { lat: Number(point?.lat), lng: Number(point?.lon ?? point?.lng) };
  if (!Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) return null;
  return lights.reduce((best, light) => {
    if (excludedIds.has(String(light.id))) return best;
    const distance = geoDistanceMeters(origin, {
      lat: Number(light.lat),
      lng: lngOf(light),
    });
    return !best || distance < best.distance ? { light, distance } : best;
  }, null)?.light || null;
}

/**
 * Placeholder signal for a distance that has no real traffic light left to
 * pair with: it is projected along the corridor at exactly that distance, the
 * same kind of manual point SYNCFIX lets you drop on the map by hand.
 */
function projectedLight(corridor, measurement, meters, index) {
  const last = corridor[corridor.length - 1];
  const previous = corridor.length > 1 ? corridor[corridor.length - 2] : null;
  let bearing = 90;
  if (previous) bearing = geoBearing(previous, last);
  else if (measurement?.first) bearing = geoBearing(measurement.first, measurement.second);
  const { lat, lon } = geoDestination(last, bearing, meters);
  return {
    id: `semafix-tratta-${index + 1}`,
    lat,
    lon,
    nome: `Semaforo ${index + 2}`,
    projected: true,
    tags: { highway: 'traffic_signals', manual: true, semafix: true },
  };
}

function normalizedLight(light) {
  return {
    ...light,
    lat: Number(light.lat),
    lon: Number(light.lon ?? light.lng),
  };
}

const pairKey = (firstId, secondId) => (
  [String(firstId), String(secondId)].sort().join('::')
);

/**
 * Measures taken in SEMAFIX, each kept with the lights its endpoints fall on
 * when they are two distinct ones. The distance is the value shown in the
 * SEMAFIX panel, so a manual correction of the input is carried over as is.
 */
function resolveMeasurements(handoff, lights) {
  return (Array.isArray(handoff?.measurements) ? handoff.measurements : [])
    .map((measurement, index) => {
      const meters = Math.round(Number(measurement?.distanceMeters));
      if (!Number.isFinite(meters) || meters <= 0) return null;
      const first = closestLight(measurement.from, lights);
      const second = closestLight(measurement.to, lights);
      const onDistinctLights = Boolean(first && second && String(first.id) !== String(second.id));
      return {
        index,
        meters,
        first: onDistinctLights ? first : null,
        second: onDistinctLights ? second : null,
      };
    })
    .filter(Boolean);
}

/**
 * Converts traffic lights fetched for a transferred SEMAFIX area into the
 * existing SYNCFIX state. The chosen SEMAFIX junction is always the reference;
 * measured endpoints determine the following selected lights and link lengths.
 */
export function hydrateSyncfixArea(handoff, fetchedLights) {
  const polygonPoints = handoffPolygonPoints(handoff);
  const fetched = (Array.isArray(fetchedLights) ? fetchedLights : [])
    .map(normalizedLight)
    .filter((light) => Number.isFinite(light.lat) && Number.isFinite(light.lon));
  if (fetched.length === 0) return null;
  const inArea = fetched.filter((light) => isPointInSyncfixPolygon(light, polygonPoints));
  const areaLights = inArea.length ? inArea : fetched;

  const reference = closestLight(
    { lat: handoff.lat, lng: handoff.lng },
    areaLights,
  );
  if (!reference) return null;
  reference.semafixIntersectionId = String(handoff.intersectionId);

  const ordered = [reference];
  const used = new Set([String(reference.id)]);
  const measurements = resolveMeasurements(handoff, fetched);

  measurements.forEach(({ first, second }) => {
    if (!first) return;
    const lastId = String(ordered[ordered.length - 1].id);
    const candidates = String(first.id) === lastId ? [second]
      : String(second.id) === lastId ? [first]
        : [first, second];
    candidates.forEach((light) => {
      if (!used.has(String(light.id))) {
        ordered.push(light);
        used.add(String(light.id));
      }
    });
  });

  // One tratta per distance set in SEMAFIX, so every value stays visible in
  // "Configura Semafori": the corridor grows with the closest signals of the
  // area, then of the whole fetched set, and finally with projected ones.
  const wantedCount = Math.max(2, measurements.length + 1);
  while (ordered.length < wantedCount) {
    const last = ordered[ordered.length - 1];
    const nearest = closestLight(last, areaLights, used) || closestLight(last, fetched, used);
    if (!nearest) break;
    ordered.push(nearest);
    used.add(String(nearest.id));
  }
  while (ordered.length < wantedCount) {
    const index = ordered.length - 1;
    const measurement = measurements[index];
    // Without a distance there is nothing to project the signal at.
    if (!measurement) break;
    ordered.push(projectedLight(ordered, measurement, measurement.meters, index));
  }

  const allLights = [
    ...areaLights,
    ...ordered.filter((light) => !areaLights.includes(light)),
  ];

  const velocity = Number(handoff.speedKph) > 0 ? Number(handoff.speedKph) : 50;
  const configs = ordered.map((light, index) => ({
    id: light.id,
    nome: index === 0
      ? (handoff.name || light.tags?.name || light.nome || 'Semaforo SEMAFIX')
      : (light.tags?.name || light.nome || `Semaforo ${index + 1}`),
    green_start: index === 0 ? Number(handoff.offset || 0) : 0,
    durata_verde: index === 0 ? Number(handoff.green || 30) : 30,
    yellow_time: index === 0 ? Number(handoff.yellow || 3) : 3,
    red_time: index === 0 ? Number(handoff.red || 30) : 30,
    lunghezza_attraversamento: index === 0 ? Number(handoff.crossingLength || 10) : 10,
    distanza: index === 0 ? 0 : Math.round(geoDistanceMeters(ordered[index - 1], light)),
    coordinates: [light.lat, light.lon],
    reference: index === 0,
    semafixIntersectionId: index === 0 ? String(handoff.intersectionId) : undefined,
  }));
  const consumed = new Set();
  const links = ordered.slice(1).map((light, index) => {
    const from = ordered[index];
    const key = pairKey(from.id, light.id);
    // A measure whose endpoints land on this pair wins; the remaining ones are
    // assigned in the order they were taken rather than being dropped.
    const measured = measurements.find((item) => (
      !consumed.has(item.index) && item.first && pairKey(item.first.id, item.second.id) === key
    )) || measurements.find((item) => !consumed.has(item.index));
    if (measured) consumed.add(measured.index);
    const distanza = measured
      ? measured.meters
      : Math.max(1, Math.round(geoDistanceMeters(from, light)));
    configs[index + 1].distanza = distanza;
    return {
      fromId: from.id,
      fromNome: configs[index].nome,
      toId: light.id,
      toNome: configs[index + 1].nome,
      distanza,
      velocita: velocity,
    };
  });

  return {
    polygonPoints,
    allLights,
    selectedLights: ordered,
    configs,
    links,
    velocity,
  };
}

/**
 * Translates a SEMAFIX corridor into the SYNCFIX seeds: one traffic light and one
 * timing config per intersection, plus the links that carry the measured spacing
 * and the progression speed used to compute the offsets.
 */
export function handoffToSyncfixSeed(handoff) {
  if (!handoff) return null;
  const stops = corridorStops(handoff).filter((stop) => Number.isFinite(Number(stop.lat)) && Number.isFinite(Number(stop.lng)));
  if (stops.length === 0) return null;

  const velocity = Number(handoff.speedKph) > 0 ? Number(handoff.speedKph) : 50;

  const lights = stops.map((stop) => ({
    id: `semafix-${stop.signalId || stop.intersectionId}`,
    lat: Number(stop.lat),
    lon: Number(stop.lng),
    nome: stop.name,
    semafixIntersectionId: String(stop.intersectionId),
    tags: { name: stop.name, manual: true, semafix: true },
  }));

  const configs = stops.map((stop, index) => ({
    id: lights[index].id,
    nome: stop.name || 'Semaforo SEMAFIX',
    green_start: Number(stop.offset || 0),
    durata_verde: Number(stop.green || 30),
    yellow_time: Number(stop.yellow || 3),
    red_time: Number(stop.red || 30),
    lunghezza_attraversamento: Number(stop.crossingLength || handoff.crossingLength || 10),
    distanza: Math.round(Number(stop.distance || 0)),
    coordinates: [Number(stop.lat), Number(stop.lng)],
    reference: index === 0,
    semafixIntersectionId: String(stop.intersectionId),
  }));

  const links = stops.slice(1).map((stop, index) => ({
    fromId: lights[index].id,
    fromNome: configs[index].nome,
    toId: lights[index + 1].id,
    toNome: configs[index + 1].nome,
    distanza: Math.max(1, Math.round(Number(stop.distance || 0))),
    velocita: velocity,
  }));

  return {
    lights,
    configs,
    links,
    velocity,
    polygonPoints: handoffPolygonPoints(handoff),
    openConfigModal: Boolean(handoff.openConfigModal),
  };
}
